/**
 * Transform utilities for converting native report API responses to chart-ready data
 */

import type { FilterCondition } from '@/lib/crm/components/SchemaFilterBuilder';
import type { FilterConditionGroup } from '@/lib/crm/filters/types';
import type {
  ChartType,
  RelativeDateValue,
  ReportViewFilter,
} from '@/types/reports';
import { viewFilterKey } from '@/types/reports';

import type {
  FilterGroup,
  NativeReportColumn,
  NativeReportQuery,
  NativeReportResponse,
} from '../../../api/reports';
import { isDateDimensionWithGranularity } from '../constants';
import {
  type CustomizeConfig,
  type GroupBySelection,
  type MeasureSelection,
  normalizeCountMeasures,
  type WizardState,
} from '../wizard/types';
import {
  dateRangeToFilters,
  matchDateRangeLabel,
  matchDateRangeLabelAnchored,
  matchRelativeDateRange,
} from './dateRangeMatching';
import { toFilterConditionGroups } from './filterConfigMigration';

/**
 * Chart data result type - matches the shape returned by resultSet.chartPivot()
 */
export type ChartDataResult = Record<string, unknown>[];

/**
 * Keys the backend emits inside each `rows` entry that are response *metadata*,
 * never user-visible data columns — they are deliberately absent from
 * `columns`. `group_raw` carries the per-row group descriptors that the
 * drill-down panel reads straight off `NativeReportResponse.rows`.
 *
 * Any code deriving display columns from row keys MUST exclude these. When
 * `group_raw` was introduced it leaked into two such derivations at once: the
 * data table rendered a bogus `GROUP_RAW` column full of `[object Object]`,
 * and the funnel picked it as its label column, so every step was labelled
 * `[object Object]`. Register new backend metadata keys here rather than in a
 * local set at each call site.
 */
export const RESPONSE_METADATA_KEYS: ReadonlySet<string> = new Set([
  'group_raw',
]);

/**
 * Keys in a chart-data row that are never a measure: the response metadata
 * above (chart data IS the raw rows verbatim when chartType is `'table'` — see
 * `nativeToChartData`) plus the bookkeeping `nativeToChartData` synthesises
 * onto cartesian and pie rows.
 */
export const NON_MEASURE_ROW_KEYS: ReadonlySet<string> = new Set([
  ...RESPONSE_METADATA_KEYS,
  'x',
  'xValues',
  '__sourceRowIndex',
]);

/**
 * Table column type - matches the shape returned by resultSet.tableColumns()
 */
export interface TableColumn {
  key: string;
  title: string;
  shortTitle: string;
  type: string;
}

/**
 * Whether a native response column is an aggregation (measure) column.
 *
 * Uses the backend's `is_aggregation` flag when available (the reliable
 * signal — native aliases are prefix-style like `sum_ahoy_amount`, so
 * suffix heuristics misclassify) and falls back to `type === 'number'`
 * for older responses that predate the flag.
 */
export function isAggregationColumn(col: NativeReportColumn): boolean {
  return 'is_aggregation' in col && col.is_aggregation !== undefined
    ? col.is_aggregation
    : col.type === 'number';
}

/**
 * Canonical sort key for a measure — the native aggregation alias
 * (`sum_ahoy_amount`, or bare `count` for the propertyless count).
 *
 * This is the SINGLE encoding shared by the Sort By options builder
 * (CustomizeStep), the query builder (buildNativeQueryFromWizardState, which
 * emits the same alias into `sort_by`), and the reverse parser
 * (parseNativeQueryToWizardState, which hydrates `orderBy.field` from
 * `sort_by`). Diverging encodings here previously dropped every
 * property-based sort from the query. Group-by sort keys are the bare
 * property name (the builder adds the `__granularity` suffix itself).
 *
 * Returns null for invalid measures (sum/avg with no property).
 */
export function measureSortKey(measure: MeasureSelection): string | null {
  if (measure.aggregation === 'count') {
    return 'count';
  }
  if (!measure.property) {
    return null;
  }
  return `${measure.aggregation}_${measure.property}`;
}

/**
 * Canonical sort key for a group-by selection — the backend's group_by
 * column name. Association group-bys sort on the association type name
 * (the column the backend emits for `{association: name}`), NOT on the
 * cosmetic `property` some templates carry alongside it (e.g. `'stage'`
 * next to `ahoy_deal_to_deal_pipeline_stage`); a sort on that property
 * name is rejected by the backend because no such column exists.
 * Time-granularity group-bys sort on `property__granularity`.
 */
export function groupBySortKey(groupBy: GroupBySelection): string {
  if (groupBy.association) {
    return groupBy.association;
  }
  if (groupBy.timeGranularity) {
    return `${groupBy.property}__${groupBy.timeGranularity}`;
  }
  return groupBy.property;
}

/**
 * Whether a (decoded) orderBy.field targets this group-by. Matches the
 * canonical key, the bare property, and — for association group-bys — the
 * cosmetic property name so reports saved with `orderBy.field: 'stage'`
 * resolve to the association column instead of erroring.
 */
function groupByMatchesSortField(
  groupBy: GroupBySelection,
  field: string
): boolean {
  if (groupBy.association) {
    return (
      field === groupBy.association ||
      (groupBy.property !== '' && field === groupBy.property)
    );
  }
  return field === groupBy.property;
}

/**
 * Strip the legacy `measure:`/`dimension:` prefixes that older saved
 * wizard_states carry in `orderBy.field`. Those prefixed ids never matched
 * the query builder, so decoding them here upgrades old reports to a working
 * sort on their next save instead of silently dropping it.
 */
function decodeLegacySortField(field: string): string {
  if (field.startsWith('measure:')) {
    return field.slice('measure:'.length);
  }
  if (field.startsWith('dimension:')) {
    return field.slice('dimension:'.length);
  }
  return field;
}

/**
 * Transform native report response to chart data
 *
 * @param response - Native report response with columns and rows
 * @param chartType - Chart type identifier (bar, line, area, pie, table)
 * @returns Chart data in the format expected by recharts
 */
export function nativeToChartData(
  response: NativeReportResponse,
  chartType: string
): ChartDataResult {
  const { columns, rows } = response;

  if (chartType === 'table') {
    return rows;
  }

  // Find group_by and measure columns
  const groupByColumns = columns.filter(col => !isAggregationColumn(col));
  const measureColumns = columns.filter(isAggregationColumn);

  // Validate rows
  const invalidRowCount = rows.filter(row => {
    return columns.some(col => {
      if (!(col.key in row)) {
        return true;
      }
      if (col.type === 'number' && row[col.key] == null) {
        return true;
      }
      return false;
    });
  }).length;

  if (invalidRowCount > rows.length * 0.1) {
    console.warn(
      `[nativeToChartData] ${invalidRowCount}/${rows.length} rows (${Math.round((invalidRowCount / rows.length) * 100)}%) missing expected columns or have null numeric values`
    );
  }

  if (chartType === 'pie') {
    // Pie chart: {name: string, [measureKey]: number}[]
    // Use actual measure key (not 'value') so tooltip formatter can detect currency types
    // PieChartCase will transform this to {name, value} for Recharts, but measureKeys
    // derivation will find the actual measure key for tooltip formatting
    const nameColumn = groupByColumns[0];
    const valueColumn = measureColumns[0];

    if (!nameColumn || !valueColumn) {
      return [];
    }

    // Use 'x' as the label key (like Cube data) so PieChartCase handles it consistently
    // downstream ChartRendererCore transforms spread ...item, so __sourceRowIndex survives reorder/topN
    // xValues carries the raw (unformatted) group value, matching the bar/line/area
    // shape below — sortChartDataChronologically reads xValues[0] to order date-
    // granularity buckets, and without it pie's chronological sort was a no-op.
    return rows.map((row, index) => ({
      x: String(row[nameColumn.key] ?? ''),
      xValues: [row[nameColumn.key] as string | null],
      [valueColumn.key]: Number(row[valueColumn.key]) || 0,
      __sourceRowIndex: index,
    }));
  }

  // Bar/Line/Area: {x: string, xValues: string[], [measureKey]: number}[]
  // Include all group_by dimensions in xValues for multi-dimension label rendering
  if (groupByColumns.length === 0) {
    return [];
  }

  // downstream ChartRendererCore transforms spread ...item, so __sourceRowIndex survives reorder/topN
  return rows.map((row, index) => {
    // Combine all group-by values into a single x-axis label (joined by " › ")
    // This preserves multi-dimension context (e.g., "Pipeline A › Stage 1")
    const xParts = groupByColumns.map(col => String(row[col.key] ?? ''));
    const xLabel = xParts.join(' › ');

    // Store individual values in xValues array for granular label resolution
    const xValues = groupByColumns.map(col => row[col.key] as string | null);

    const dataPoint: Record<string, unknown> = {
      x: xLabel,
      xValues,
      __sourceRowIndex: index,
    };

    // Add each measure as a numeric field
    measureColumns.forEach(col => {
      dataPoint[col.key] = Number(row[col.key]) || 0;
    });

    return dataPoint;
  });
}

/**
 * Re-order cartesian chart rows so a date-granularity primary x-axis renders
 * chronologically (ascending), with null/missing date buckets last. No-op when
 * the primary dimension is not a date granularity — non-date axes keep the
 * query's row order exactly. Sorts on the RAW ISO x value held in xValues[0]
 * (full ISO datetime strings sort lexicographically == chronologically, across
 * year boundaries and for every granularity), never the formatted label.
 * ALWAYS ascending by design: a time series sorted by measure value on screen
 * is never meaningful, and this is what lets existing measure-desc reports
 * render chronologically without a resave. Stable + non-mutating (spreads before
 * sort); buckets with equal or absent keys keep their relative order.
 */
function nullLastStringCompare(av: string | null, bv: string | null): number {
  if (av === null && bv === null) {
    return 0;
  }
  if (av === null) {
    return 1;
  }
  if (bv === null) {
    return -1;
  }
  if (av < bv) {
    return -1;
  }
  if (av > bv) {
    return 1;
  }
  return 0;
}

export function sortChartDataChronologically(
  chartData: ChartDataResult,
  primaryDimensionName: string | undefined
): ChartDataResult {
  if (
    !primaryDimensionName ||
    !isDateDimensionWithGranularity(primaryDimensionName)
  ) {
    return chartData;
  }
  const rawKey = (row: Record<string, unknown>): string | null => {
    const xValues = row.xValues as (string | null)[] | undefined;
    const raw = xValues?.[0];
    if (raw == null || raw === '' || raw === 'null' || raw === '∅') {
      return null;
    }
    return String(raw);
  };
  return [...chartData].sort((a, b) =>
    nullLastStringCompare(rawKey(a), rawKey(b))
  );
}

/**
 * Re-order table/funnel rows (nativeToTablePivot output) so a date-granularity
 * primary group-by column renders chronologically. Same semantics as
 * sortChartDataChronologically (ascending, null/missing buckets last, stable,
 * non-mutating, no-op for a non-date primary dimension) but reads the raw
 * value directly off `primaryColumnKey` — table and funnel render
 * nativeToTablePivot's stringified rows, which carry no `xValues`, so the
 * cartesian/pie sort above never touched them (this was the gap: pie, table,
 * and funnel were all listed as chronologically-sorted chart types, but only
 * pie's data even carried a sortable raw value, and pie needed its own fix —
 * see nativeToChartData's pie branch).
 *
 * Reordering breaks the positional 1:1 alignment between these display rows and
 * `NativeReportResponse.rows` that the drill path relies on (ReportDrillPanel
 * indexes `nativeData.rows` by the selection's rowIndex, which is the ORIGINAL
 * order). So each reordered row is stamped with `__sourceRowIndex` — its
 * position in the unsorted input — exactly as `nativeToChartData` stamps chart
 * rows for `applyTopN`. Consumers (ReportDataTable, FunnelChartCase) resolve
 * drill/selection through that stamp rather than the post-sort position, and
 * fall back to the positional index on the no-op path where no stamp is added.
 */
export function sortTableDataChronologically(
  tableData: Array<Record<string, unknown>>,
  primaryDimensionName: string | undefined,
  primaryColumnKey: string | undefined
): Array<Record<string, unknown>> {
  if (
    !primaryDimensionName ||
    !isDateDimensionWithGranularity(primaryDimensionName) ||
    !primaryColumnKey
  ) {
    return tableData;
  }
  const rawKey = (row: Record<string, unknown>): string | null => {
    const raw = row[primaryColumnKey];
    if (raw == null || raw === '' || raw === 'null' || raw === '∅') {
      return null;
    }
    return String(raw);
  };
  return tableData
    .map((row, index) => ({ ...row, __sourceRowIndex: index }))
    .sort((a, b) => nullLastStringCompare(rawKey(a), rawKey(b)));
}

/**
 * Transform native report columns to table column format
 *
 * @param response - Native report response with columns
 * @returns Table columns in the format expected by ChartRenderer
 */
export function nativeToTableColumns(
  response: NativeReportResponse
): TableColumn[] {
  return response.columns.map(col => ({
    key: col.key,
    title: col.label,
    shortTitle: col.label,
    type: col.type,
  }));
}

/**
 * Transform native report response to table pivot format (stringified values)
 *
 * @param response - Native report response with rows
 * @returns Table rows with all values stringified (null values converted to "null" string
 *          which formatCellValue recognizes as missing data and renders as em-dash),
 *          excluding RESPONSE_METADATA_KEYS — consumers derive their columns from
 *          these row keys, and stringifying a metadata object yields "[object Object]"
 */
export function nativeToTablePivot(
  response: NativeReportResponse
): Array<Record<string, string>> {
  return response.rows.map(row => {
    const stringifiedRow: Record<string, string> = {};
    Object.entries(row).forEach(([key, value]) => {
      // Response metadata is not display data: it has no entry in `columns`,
      // and String()-ing it would render "[object Object]" in every consumer
      // that builds its column list from row keys.
      if (RESPONSE_METADATA_KEYS.has(key)) {
        return;
      }
      // Use "null" string sentinel for null values so formatCellValue can render em-dash
      // (matches Cube.js tablePivot behavior where null becomes literal "null" string)
      stringifiedRow[key] = value == null ? 'null' : String(value);
    });
    return stringifiedRow;
  });
}

/**
 * Operators that are not supported by the native backend.
 * The frontend filter UI may allow these, but native reports cannot process them.
 */
const UNSUPPORTED_OPERATORS = new Set<string>();

/**
 * Map frontend operator to backend operator format.
 * Note: is_empty and is_not_empty both map to 'isnull' — the value
 * (true/false) determines direction. See normalizeFilterValue().
 *
 * @throws Error if the operator is not supported by native reporting
 */
function mapOperatorToBackend(operator: string): string {
  if (UNSUPPORTED_OPERATORS.has(operator)) {
    throw new Error(
      `Filter operator "${operator}" is not supported by native reporting. ` +
        'Please use a different filter operator.'
    );
  }

  const operatorMap: Record<string, string> = {
    equals: 'exact',
    not_equals: 'ne',
    contains: 'contains',
    not_contains: 'not_contains',
    starts_with: 'startswith',
    ends_with: 'endswith',
    greater_than: 'gt',
    less_than: 'lt',
    greater_than_or_equal: 'gte',
    less_than_or_equal: 'lte',
    is_empty: 'isnull',
    is_not_empty: 'isnull',
    in: 'in',
    not_in: 'notin',
  };

  return operatorMap[operator] || operator;
}

/**
 * Normalize filter value based on operator.
 * For is_empty/is_not_empty, the backend expects a boolean value:
 *   is_empty → isnull with value true
 *   is_not_empty → isnull with value false
 */
function normalizeFilterValue(operator: string, value: unknown): unknown {
  if (operator === 'is_empty') {
    return true;
  }
  if (operator === 'is_not_empty') {
    return false;
  }
  return value;
}

/**
 * True when a condition is still being authored and cannot execute yet:
 * no filter target chosen, or an operator that needs a value has none
 * (blank text, empty list, or a currency quantity without an amount).
 * These are draft rows — sending them produces a backend
 * "Invalid query parameters" error mid-edit, so the query builder treats
 * them as not-yet-filters and leaves them out.
 */
function isIncompleteCondition(condition: FilterCondition): boolean {
  if (!condition.field && !condition.association) {
    return true;
  }
  // Presence operators carry their value in the operator itself.
  if (
    condition.operator === 'is_empty' ||
    condition.operator === 'is_not_empty'
  ) {
    return false;
  }
  const { value } = condition;
  // Whitespace-only counts as blank — mirrors the shared builder's own
  // incompleteness rules so a space never becomes a real filter.
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return true;
  }
  if (
    Array.isArray(value) &&
    value.filter(entry => String(entry).trim() !== '').length === 0
  ) {
    return true;
  }
  if (typeof value === 'object' && 'amount' in value) {
    const { amount } = value as { amount?: unknown };
    if (amount === null || amount === undefined || amount === '') {
      return true;
    }
  }
  return false;
}

/**
 * Convert a single wizard filter condition to its backend representation.
 *
 * Supports both property-based and association-based filters. Association
 * filters use the `association` key instead of `property`.
 */
function filterConditionToBackend(condition: FilterCondition): {
  property?: string;
  association?: string;
  target_property?: string;
  operator: string;
  value: unknown;
} {
  const operator = mapOperatorToBackend(condition.operator);
  const value = normalizeFilterValue(condition.operator, condition.value);

  if (condition.association) {
    return {
      association: condition.association,
      operator,
      value,
      ...(condition.target_property
        ? { target_property: condition.target_property }
        : {}),
    };
  }
  return { property: condition.field, operator, value };
}

/**
 * Stable identity key for a backend filter, used to detect filters shared
 * across multiple filter groups.
 */
function filterIdentity(f: {
  property?: string;
  association?: string;
  target_property?: string;
  operator: string;
  value: unknown;
}): string {
  return `${f.target_property ?? ''}|${f.association ?? ''}|${f.property ?? ''}|${f.operator}|${JSON.stringify(
    f.value ?? null
  )}`;
}

/**
 * Map backend filter operator back to frontend operator.
 * Inverse of mapOperatorToBackend().
 */
function mapOperatorFromBackend(operator: string, value: unknown): string {
  // isnull maps to is_empty or is_not_empty depending on value
  if (operator === 'isnull') {
    return value === true ? 'is_empty' : 'is_not_empty';
  }

  const reverseMap: Record<string, string> = {
    exact: 'equals',
    ne: 'not_equals',
    contains: 'contains',
    not_contains: 'not_contains',
    startswith: 'starts_with',
    endswith: 'ends_with',
    gt: 'greater_than',
    lt: 'less_than',
    gte: 'greater_than_or_equal',
    lte: 'less_than_or_equal',
    in: 'in',
    notin: 'not_in',
  };

  return reverseMap[operator] || operator;
}

const VALID_AGGREGATIONS = new Set(['count', 'sum', 'avg']);
const VALID_GRANULARITIES = new Set([
  'day',
  'week',
  'month',
  'quarter',
  'year',
]);

/**
 * Narrow an opaque saved-report `query` to a NativeReportQuery.
 *
 * A report's `query` may be a NativeReportQuery, a legacy Cube query object, or
 * null. The native parser keys off `entity_type_name` and `aggregations`; a
 * legacy object lacking those silently parses into an invalid wizard state
 * rather than throwing, so callers must guard with this before parsing and fall
 * back to "start fresh" when it returns false.
 *
 * The aggregations array must be non-empty: a native report always carries at
 * least one measure (buildNativeQueryFromWizardState returns null when there
 * are no measures). An empty aggregations array would hydrate step-3 wizard
 * state with no measures — an invalid report — so it must fall through to the
 * safe reset instead.
 */
export function isNativeReportQuery(
  query: unknown
): query is NativeReportQuery {
  if (typeof query !== 'object' || query === null) {
    return false;
  }
  const candidate = query as Partial<NativeReportQuery>;
  return (
    typeof candidate.entity_type_name === 'string' &&
    candidate.entity_type_name.length > 0 &&
    Array.isArray(candidate.aggregations) &&
    candidate.aggregations.length > 0
  );
}

/**
 * Reconstruct dateRange filters that buildNativeQueryFromWizardState()
 * distributed into EVERY backend filter group, folding them back onto the
 * matching groupBy selection (mutated in place) so a multi-group query that
 * scopes by a date range round-trips losslessly instead of showing the date
 * range as a literal condition in every group.
 *
 * @param groups - The query's filter_groups (non-empty)
 * @param groupBy - Parsed groupBy selections; entries are mutated in place
 *   to attach a reconstructed `dateRange`
 * @returns Identity keys (see filterIdentity()) of filters folded into a
 *   dateRange — callers strip these out of the emitted filter conditions
 */
function reconstructDateRangeFilters(
  groups: FilterGroup[],
  groupBy: GroupBySelection[],
  referenceDate?: Date
): Set<string> {
  // Filters present in EVERY group (the distributed dateRange filters).
  // Build a count map in one O(F×G) pass, then keep filters from group 0
  // whose identity appears in all groups. The single-group case is covered
  // by the same logic (every filter of the lone group is trivially shared) —
  // it used to be excluded because same-day-only label matching made
  // reconstruction unreliable for reopened reports, but anchored matching
  // (referenceDate) makes the common no-OR shape reconstruct correctly too.
  const sharedFilters: (typeof groups)[0]['filters'] = [];
  const occurrences = new Map<string, number>();
  for (const g of groups) {
    // Count each identity at most once per group: a filter duplicated
    // within a single OR branch must not be mistaken for one shared across
    // every branch. Without the per-group dedup, `[X, X]` in one group and
    // `[X]` in another counts X three times (never "shared"), and `[X, X]`
    // plus an unrelated group counts X twice and wrongly strips it on save.
    for (const key of new Set(g.filters.map(filterIdentity))) {
      occurrences.set(key, (occurrences.get(key) ?? 0) + 1);
    }
  }
  for (const f of groups[0].filters) {
    if (occurrences.get(filterIdentity(f)) === groups.length) {
      sharedFilters.push(f);
    }
  }

  // Per-property dateRange reconstruction (not all-or-nothing): group the
  // shared gte/lte-on-a-property filters by property; any shared filter
  // that isn't a gte/lte-on-a-property stays as a genuine condition.
  const byProperty = new Map<
    string,
    Array<{ operator: string; value: unknown }>
  >();
  const sharedByProperty = new Map<string, typeof sharedFilters>();
  for (const f of sharedFilters) {
    if (f.property && (f.operator === 'gte' || f.operator === 'lte')) {
      const dateList = byProperty.get(f.property) ?? [];
      dateList.push({ operator: f.operator, value: f.value });
      byProperty.set(f.property, dateList);

      const filterList = sharedByProperty.get(f.property) ?? [];
      filterList.push(f);
      sharedByProperty.set(f.property, filterList);
    }
  }

  const strippableKeys = new Set<string>();
  for (const [property, dateFilters] of byProperty) {
    // Match anchored at the SAVE day (referenceDate = the report's
    // updated_at, on the UTC calendar — the shared calendar all range
    // boundaries are derived on), tolerating ±1 day to recover queries
    // saved before the UTC switch, whose literals came from the saver's
    // local calendar day. Literals that equaled a named range on the day
    // the query was written are the range by construction, so
    // reclassifying them as rolling is safe.
    //
    // Deliberately NO fall-back to a today-anchored match when a reference
    // exists: hand-written fixed dates that merely COINCIDE with a named
    // range as of the REOPEN day would be mislabeled as rolling, and the
    // next save would silently shift them. When no reference is available
    // (a query generated in this session, e.g. the AI slideout), "today"
    // IS the write day, so the plain match is the anchored match.
    //
    // Relative TOKENS are tried first and need no anchor at all: the match is
    // structural, so it is exact by construction and cannot mislabel a window.
    // Only a legacy absolute pair falls through to the anchored literal
    // matchers below.
    const label =
      matchRelativeDateRange(property, dateFilters) ??
      (referenceDate
        ? matchDateRangeLabelAnchored(property, dateFilters, referenceDate)
        : matchDateRangeLabel(property, dateFilters));
    // Match a groupBy on this property that has no dateRange yet. A
    // timeGranularity must NOT disqualify it: the wizard's dateRange dropdown
    // only renders for time-type fields, and every time-type groupBy is
    // seeded with a timeGranularity ('day'). Excluding timeGranularity entries
    // would therefore make reconstruction impossible for the only groupBys
    // that can carry a dateRange, so a time-grouped OR report would lose its
    // date-range chip on reopen (it would return as literal Customize filters).
    const target = groupBy.find(g => g.property === property && !g.dateRange);
    if (label && target) {
      target.dateRange = label;
      for (const f of sharedByProperty.get(property) ?? []) {
        strippableKeys.add(filterIdentity(f));
      }
    }
    // Otherwise leave this property's shared filters in place — they stay
    // as genuine conditions rather than being silently dropped.
  }

  return strippableKeys;
}

/**
 * Parse a NativeReportQuery back into wizard state.
 *
 * This is the inverse of buildNativeQueryFromWizardState(). Used when loading
 * a report created by the AI (query_report tool) or any native report that
 * was saved without wizard_state.
 *
 * @param query - The native report query to parse
 * @param chartType - The chart type stored on the report
 * @param options.referenceDate - The day the query was saved (typically the
 *   report's updated_at). Relative date-range labels are matched as of this
 *   day, so a "Last 7 days" chip is reconstructed — and stays rolling — even
 *   when the report is reopened later. Without it, matching anchors to today
 *   and only same-day reopens reclaim the chip.
 * @returns A partial WizardState suitable for merging
 */
export function parseNativeQueryToWizardState(
  query: NativeReportQuery,
  chartType: ChartType | '' = '',
  options?: { referenceDate?: Date }
): Partial<WizardState> {
  // --- Data source ---
  const dataSource = {
    primaryObjectType: query.entity_type_name,
    relatedObjectTypes: [] as string[],
  };

  // --- Measures (aggregations) ---
  const measures: MeasureSelection[] = normalizeCountMeasures(
    (query.aggregations ?? [])
      .filter(agg => VALID_AGGREGATIONS.has(agg.function))
      .map(agg => ({
        property: agg.property ?? '',
        aggregation: agg.function as 'count' | 'sum' | 'avg',
      }))
  );

  // --- Group by ---
  const groupBy: GroupBySelection[] = (query.group_by ?? []).map(item => {
    if (typeof item === 'string') {
      return { property: item };
    }
    if ('association' in item && item.association) {
      return { property: '', association: item.association };
    }
    const entry: GroupBySelection = {
      property: item.property ?? '',
    };
    if (
      item.time_granularity &&
      VALID_GRANULARITIES.has(item.time_granularity)
    ) {
      entry.timeGranularity =
        item.time_granularity as GroupBySelection['timeGranularity'];
    }
    return entry;
  });

  // --- Filters ---
  // The backend ORs filter groups together and ANDs within a group — the
  // wizard's group-native model represents that directly, one
  // FilterConditionGroup per backend group, so every shape round-trips
  // losslessly with no AND/OR detection needed.
  //
  // The forward build (buildNativeQueryFromWizardState) distributes the
  // dateRange-derived filters from a groupBy into EVERY group when there is
  // more than one, so a multi-group query that also scopes by a date range
  // looks like multiple groups that each share the same date filters plus
  // their own unique conditions. Reconstruct those shared filters back onto
  // the matching groupBy.dateRange (per property, independently) so the
  // round-trip is lossless and the date range doesn't show up as a literal
  // condition in every group.
  let filterId = 0;
  const toCondition = (f: {
    property?: string;
    association?: string;
    target_property?: string;
    operator: string;
    value: unknown;
  }): FilterCondition => {
    filterId += 1;
    return {
      id: `filter-${filterId}`,
      field: f.property ?? '',
      association: f.association,
      target_property: f.target_property,
      operator: mapOperatorFromBackend(
        f.operator,
        f.value
      ) as FilterCondition['operator'],
      value: (f.value ?? null) as FilterCondition['value'],
    };
  };

  let filterGroupsOut: FilterConditionGroup[] = [];
  if (query.filter_groups && query.filter_groups.length > 0) {
    const groups = query.filter_groups;
    const strippableKeys = reconstructDateRangeFilters(
      groups,
      groupBy,
      options?.referenceDate
    );

    filterGroupsOut = groups.map((g, i) => ({
      id: `group-${i}`,
      conditions: g.filters
        .filter(f => !strippableKeys.has(filterIdentity(f)))
        .map(toCondition),
    }));

    // Drop groups that became empty after stripping reconstructed date
    // filters, re-indexing ids so they stay contiguous.
    const nonEmpty = filterGroupsOut.filter(gr => gr.conditions.length > 0);
    if (nonEmpty.length === 0) {
      filterGroupsOut = [];
    } else if (nonEmpty.length !== filterGroupsOut.length) {
      filterGroupsOut = nonEmpty.map((gr, i) => ({
        ...gr,
        id: `group-${i}`,
      }));
    } else {
      filterGroupsOut = nonEmpty;
    }
  }

  // --- Sort / Order ---
  // Normalize ``property__granularity`` sort keys back to the plain property
  // name so the round-trip survives. The forward build encodes a sort on a
  // time-granularity-grouped property as ``property__granularity`` (e.g.
  // ``ahoy_entered_at__week``) via groupBySortKey, but the wizard's
  // orderBy.field carries just the property; on re-save buildNativeQueryFromWizardState
  // matches by ``g.property === field`` and would otherwise miss the
  // groupBy match and fall through to the ambiguous fallback path.
  // Association sort keys (the association type name) are kept verbatim —
  // that IS the canonical orderBy.field for an association group-by.
  let orderBy: CustomizeConfig['orderBy'] = null;
  if (query.sort_by) {
    let field = query.sort_by;
    // AI/native queries are free to use ANY aggregation alias (e.g.
    // 'deal_count'); the wizard's world only knows the canonical
    // measureSortKey encoding ('count' / 'sum_ahoy_amount'). Normalize a
    // sort on a custom alias to the canonical key of the SAME aggregation,
    // otherwise the round-trip build (which re-emits canonical aliases)
    // finds no match and silently drops the sort on save.
    const aliasMatch = (query.aggregations ?? []).find(
      agg => agg.alias === field && VALID_AGGREGATIONS.has(agg.function)
    );
    if (aliasMatch) {
      const canonical = measureSortKey({
        property: aliasMatch.property ?? '',
        aggregation: aliasMatch.function as MeasureSelection['aggregation'],
      });
      if (canonical) {
        field = canonical;
      }
    } else {
      const sepIdx = field.lastIndexOf('__');
      if (sepIdx > 0) {
        const baseProp = field.slice(0, sepIdx);
        const suffix = field.slice(sepIdx + 2);
        if (
          VALID_GRANULARITIES.has(suffix) &&
          groupBy.some(
            g => g.property === baseProp && g.timeGranularity === suffix
          )
        ) {
          field = baseProp;
        }
      }
    }
    orderBy = {
      field,
      direction: query.order ?? 'desc',
    };
  }

  const customize: CustomizeConfig = {
    filters: filterGroupsOut,
    // An empty string is treated the same as an omitted chartType and
    // defaults to 'table'.
    chartType: chartType || 'table',
    orderBy,
    limit: query.limit ?? null,
    ...(query.view_filters && query.view_filters.length > 0
      ? {
          viewFilters: query.view_filters.map(vf => ({
            ...(vf.association !== undefined
              ? { association: vf.association }
              : {}),
            ...(vf.property !== undefined ? { property: vf.property } : {}),
            label: vf.label,
            defaultValue: vf.default_value ?? null,
          })),
        }
      : {}),
  };

  return {
    dataSource,
    fields: { measures, groupBy },
    customize,
  };
}

/**
 * Build native query from wizard state
 *
 * @param wizardState - Current wizard state
 * @param entityTypeToCubeIgnored - Ignored parameter for compatibility
 * @returns Native report query or null if wizard state is incomplete
 */
export function buildNativeQueryFromWizardState(
  wizardState: WizardState,
  _entityTypeToCubeIgnored?: unknown
): NativeReportQuery | null {
  const { dataSource, fields, customize } = wizardState;

  // Check for incomplete state
  if (!dataSource.primaryObjectType || fields.measures.length === 0) {
    return null;
  }

  // Build aggregations from measures
  const aggregations: Array<{
    function: string;
    alias: string;
    property?: string;
  }> = normalizeCountMeasures(fields.measures).flatMap(measure => {
    // Count = count object instances; the backend labels the column with
    // the entity plural ("Count of Contacts"). normalizeCountMeasures has
    // already stripped any stray property and deduped to at most one.
    if (measure.aggregation === 'count') {
      // Alias must equal measureSortKey(measure) — sort_by matches on it.
      return [{ function: 'count', alias: 'count' }];
    }
    if (!measure.property) {
      // sum/avg always require a property (enforced by validateWizardStateFields).
      // Guard defensively: skip this measure rather than producing "sum_undefined".
      console.warn(
        '[reports] buildNativeQueryFromWizardState: skipping measure with missing property',
        measure
      );
      return [];
    }
    return [
      {
        function: measure.aggregation,
        property: measure.property,
        // Same encoding as measureSortKey(measure) — sort_by matches on it.
        alias: `${measure.aggregation}_${measure.property}`,
      },
    ];
  });

  // Build group_by from groupBy selections
  const group_by =
    fields.groupBy.length > 0
      ? fields.groupBy.map(g => {
          if (g.association) {
            return { association: g.association };
          }
          if (g.timeGranularity) {
            return {
              property: g.property,
              time_granularity: g.timeGranularity,
            };
          }
          return g.property;
        })
      : undefined;

  // Convert dateRange from groupBy entries into property filters.
  // The FieldsStep date range dropdown sets dateRange on the groupBy,
  // but the native backend needs these as explicit filter conditions.
  const dateRangeFilterItems: Array<{
    property: string;
    operator: string;
    value: string | RelativeDateValue;
  }> = [];
  for (const g of fields.groupBy) {
    if (g.dateRange && g.property) {
      dateRangeFilterItems.push(...dateRangeToFilters(g.property, g.dateRange));
    }
  }

  // Build filter_groups from the group-native filter representation: one
  // backend AND-group per user group, ORed together. Each condition is mapped
  // to its backend shape, and the dateRange-derived filters (from groupBy) are
  // distributed into EVERY group so they AND with every OR branch — i.e.
  // (A OR B) AND date becomes (A AND date) OR (B AND date).
  const userGroups = toFilterConditionGroups(
    customize.filters,
    customize.filterLogic
  );
  const backendGroups: FilterGroup[] = userGroups
    .map(group => ({
      // Drop in-progress rows: no target selected yet, or a value-requiring
      // operator whose value is still empty. Sending them would 400 the
      // preview on every keystroke of filter authoring.
      conditions: group.conditions.filter(c => !isIncompleteCondition(c)),
    }))
    .filter(group => group.conditions.length > 0)
    .map(group => ({
      filters: [
        ...group.conditions.map(filterConditionToBackend),
        ...dateRangeFilterItems,
      ],
      operator: 'AND' as const,
    }));

  let filter_groups: FilterGroup[] | undefined;
  if (backendGroups.length > 0) {
    filter_groups = backendGroups;
  } else if (dateRangeFilterItems.length > 0) {
    filter_groups = [
      {
        filters: dateRangeFilterItems,
        operator: 'AND' as const,
      },
    ];
  } else {
    filter_groups = undefined;
  }

  // Build sort_by and order from orderBy
  let sort_by: string | undefined;
  let order: 'asc' | 'desc' | undefined;

  if (customize.orderBy) {
    const { direction } = customize.orderBy;
    // Decode legacy 'measure:'/'dimension:' prefixed ids from older saved
    // wizard_states. A decoded measure field is the bare property name,
    // which the property-match fallback below resolves to its alias. The
    // prefix itself is remembered: it asserts the sort target IS a measure,
    // which lets the fallback resolve an otherwise-ambiguous property
    // (multiple aggregations of the same property) to the first one —
    // matching what CustomizeStep displays — instead of dropping the sort.
    const wasMeasurePrefixed = customize.orderBy.field.startsWith('measure:');
    const wasDimensionPrefixed =
      customize.orderBy.field.startsWith('dimension:');
    const field = decodeLegacySortField(customize.orderBy.field);

    // Try exact match first (native alias or property name). A legacy
    // prefix asserts WHICH side the sort targets, so a 'measure:' field
    // never resolves to a same-named group-by (and vice versa) — without
    // this, a measure sort on a property that is also grouped silently
    // became a dimension sort.
    const exactAggMatch = wasDimensionPrefixed
      ? undefined
      : aggregations.find(agg => agg.alias === field);
    const exactGroupMatch = wasMeasurePrefixed
      ? undefined
      : fields.groupBy.find(g => groupByMatchesSortField(g, field));

    if (exactAggMatch) {
      sort_by = exactAggMatch.alias;
      order = direction;
    } else if (exactGroupMatch) {
      // Canonical backend column: association name, or
      // property[__granularity].
      sort_by = groupBySortKey(exactGroupMatch);
      order = direction;
    } else {
      // Fallback: field may be a Cube-style key (e.g., "AhoyDeal.ahoy_amount_sum")
      // Extract the property portion after the last dot, then match exactly
      // to avoid prefix collisions (e.g., "status" matching "deal_status")
      const fieldParts = field.split('.');
      const fieldProperty = fieldParts[fieldParts.length - 1];

      // For aggregations, match Cube-style keys like "ahoy_amount_sum" to our alias "sum_ahoy_amount"
      // Cube format: property_function (e.g., "ahoy_amount_sum")
      // Native format: function_property (e.g., "sum_ahoy_amount")
      const aggByProperty = aggregations.find(agg => {
        if (wasDimensionPrefixed || !agg.property) {
          return false;
        }
        // Match exact Cube format: property_function (e.g., "ahoy_amount_sum")
        // This is unambiguous because it matches both property AND function
        if (fieldProperty === `${agg.property}_${agg.function}`) {
          return true;
        }
        // Fallback: exact property match only (when there's no function suffix)
        if (fieldProperty === agg.property) {
          // A legacy 'measure:'-prefixed id asserts the target is a
          // measure but not WHICH aggregation — resolve ambiguity to the
          // first aggregation on the property (find() order), which is
          // exactly the measure CustomizeStep's sort dropdown displays for
          // the legacy id. Dropping the sort was strictly worse.
          if (wasMeasurePrefixed) {
            return true;
          }
          // Un-prefixed fields stay conservative: only match when this is
          // the ONLY aggregation on the property, to avoid guessing.
          const countSameProperty = aggregations.filter(
            a => a.property === agg.property
          ).length;
          return countSameProperty === 1;
        }
        return false;
      });

      // For group_by, match exact property name ('measure:' fields never
      // fall back to a dimension sort — see the exact-match guards above).
      const groupByProperty = wasMeasurePrefixed
        ? undefined
        : fields.groupBy.find(g => groupByMatchesSortField(g, fieldProperty));

      if (aggByProperty) {
        sort_by = aggByProperty.alias;
        order = direction;
      } else if (groupByProperty) {
        sort_by = groupBySortKey(groupByProperty);
        order = direction;
      }
    }
  }

  // Viewer-adjustable filters persist with the query (the report's single
  // source of truth) so every consumer — dashboard tiles, share pages —
  // knows which controls to render.
  const view_filters =
    customize.viewFilters && customize.viewFilters.length > 0
      ? customize.viewFilters.map(vf => ({
          ...(vf.association !== undefined
            ? { association: vf.association }
            : {}),
          ...(vf.property !== undefined ? { property: vf.property } : {}),
          label: vf.label,
          default_value: vf.defaultValue,
        }))
      : undefined;

  return {
    entity_type_name: dataSource.primaryObjectType,
    aggregations,
    group_by,
    filter_groups,
    sort_by,
    order,
    // Validate limit: must be a finite positive number, otherwise default to 500
    limit:
      typeof customize.limit === 'number' &&
      Number.isFinite(customize.limit) &&
      customize.limit > 0
        ? customize.limit
        : 500,
    ...(view_filters ? { view_filters } : {}),
  };
}

const VIEW_FILTER_RANGE_PATTERN =
  /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

/**
 * Parse a time-range viewer-filter selection ('YYYY-MM-DD..YYYY-MM-DD').
 * Returns null for anything malformed (including a start after the end).
 */
export function parseViewFilterRange(
  value: string
): { start: string; end: string } | null {
  const match = VIEW_FILTER_RANGE_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const [, start, end] = match;
  return start <= end ? { start, end } : null;
}

/**
 * Merge viewer filter selections into a query for execution.
 *
 * Each active selection becomes an association filter AND-ed into every
 * filter group (mirroring how dateRange-derived filters distribute), so it
 * narrows the result regardless of the author's OR structure. Returns a new
 * query — never mutates the input (it may be a stored report payload).
 */
export function applyViewFilterSelections(
  query: NativeReportQuery,
  selections: Record<string, string | null | undefined>
): NativeReportQuery {
  const active = (query.view_filters ?? [])
    .map(vf => {
      // A key PRESENT in `selections` is the viewer's explicit choice — a
      // null/undefined value there means "cleared to All" and must NOT fall
      // back to the stored default. An ABSENT key means "untouched" and
      // resolves to the stored default.
      const key = viewFilterKey(vf);
      const raw = key in selections ? selections[key] : vf.default_value;
      return { filter: vf, value: raw ?? null };
    })
    .filter(
      (s): s is { filter: ReportViewFilter; value: string } => s.value !== null
    );
  if (active.length === 0) {
    return query;
  }
  const extraFilters = active.flatMap((s): FilterGroup['filters'] => {
    if (s.filter.association !== undefined) {
      return [
        {
          association: s.filter.association,
          operator: 'exact',
          value: s.value,
        },
      ];
    }
    if (s.filter.property !== undefined) {
      // Time-range selections are encoded 'YYYY-MM-DD..YYYY-MM-DD'. A
      // malformed value is ignored rather than sent to the backend to fail.
      const range = parseViewFilterRange(s.value);
      if (!range) {
        return [];
      }
      return [
        { property: s.filter.property, operator: 'gte', value: range.start },
        { property: s.filter.property, operator: 'lte', value: range.end },
      ];
    }
    return [];
  });
  if (extraFilters.length === 0) {
    return query;
  }
  const baseGroups =
    query.filter_groups && query.filter_groups.length > 0
      ? query.filter_groups
      : [{ filters: [] }];
  return {
    ...query,
    filter_groups: baseGroups.map(group => ({
      ...group,
      filters: [...(group.filters ?? []), ...extraFilters],
    })) as NativeReportQuery['filter_groups'],
  };
}
